import type { FC } from "react";

import { FieldError } from "@/shared/components/ui/FieldError";
import { CurrencyInput } from "@/shared/components/ui/CurrencyInput";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { UploadZone } from "@/shared/components/ui/upload-zone";
import { currencyInput } from "@/shared/utils/currencyInput";

export interface LtcInsuranceFormValues {
  companyName: string;
  policyNumber: string;
  dailyBenefitAmount: string;
  benefitPeriod: string;
  documentFile: File | null;
}

export const EMPTY_LTC_INSURANCE_FORM: LtcInsuranceFormValues = {
  companyName: "",
  policyNumber: "",
  dailyBenefitAmount: "",
  benefitPeriod: "",
  documentFile: null,
};

export interface LtcInsuranceFieldErrors {
  companyName?: string;
}

interface LtcInsuranceFormProps {
  form: LtcInsuranceFormValues;
  onChange: (form: LtcInsuranceFormValues) => void;
  fieldErrors?: LtcInsuranceFieldErrors;
  showDocumentUpload?: boolean;
  existingDocumentFileName?: string | null;
  onDocumentRemoved?: () => void;
}

export const LtcInsuranceForm: FC<LtcInsuranceFormProps> = ({
  form,
  onChange,
  fieldErrors,
  showDocumentUpload = true,
  existingDocumentFileName,
  onDocumentRemoved,
}) => {
  const benefitInput = currencyInput(form.dailyBenefitAmount, (val) =>
    onChange({ ...form, dailyBenefitAmount: val }),
  );

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <Label className="text-sm font-medium" required>
          Insurance company
        </Label>
        <Input
          placeholder="Enter company name"
          value={form.companyName}
          onChange={(e) => onChange({ ...form, companyName: e.target.value })}
          className={fieldErrors?.companyName ? "border-error" : ""}
        />
        <FieldError message={fieldErrors?.companyName} />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <Label className="text-sm font-medium">
            Policy number <span className="text-tertiary">(optional)</span>
          </Label>
          <Input
            placeholder="e.g., LTC-123456"
            value={form.policyNumber}
            onChange={(e) => onChange({ ...form, policyNumber: e.target.value })}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-sm font-medium">
            Daily benefit amount <span className="text-tertiary">(optional)</span>
          </Label>
          <CurrencyInput
            value={benefitInput.displayValue}
            onChange={benefitInput.handleChange}
            placeholder="0.00"
          />
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label className="text-sm font-medium">
          Benefit period remaining <span className="text-tertiary">(optional)</span>
        </Label>
        <Input
          placeholder="e.g., 3 years"
          value={form.benefitPeriod}
          onChange={(e) => onChange({ ...form, benefitPeriod: e.target.value })}
        />
      </div>

      {showDocumentUpload && (
        <UploadZone
          label="Do you have the policy document?"
          labelSuffix="(optional)"
          file={form.documentFile}
          onFileSelect={(file) => onChange({ ...form, documentFile: file })}
          onFileRemove={() => {
            onChange({ ...form, documentFile: null });
            onDocumentRemoved?.();
          }}
          existingFileName={existingDocumentFileName}
        />
      )}
    </div>
  );
};

export function validateLtcInsuranceFields(form: LtcInsuranceFormValues): LtcInsuranceFieldErrors {
  if (!form.companyName.trim()) return { companyName: "Please enter the insurance company name" };
  return {};
}

export function parseItemToForm(
  itemName: string,
  balance: number,
  metadata?: Record<string, unknown>,
): LtcInsuranceFormValues {
  return {
    companyName: itemName,
    policyNumber: (metadata?.policy_number as string) || "",
    dailyBenefitAmount: balance ? String(balance) : "",
    benefitPeriod: (metadata?.benefit_period as string) || "",
    documentFile: null,
  };
}

export function buildMetadata(form: LtcInsuranceFormValues): Record<string, unknown> {
  return {
    policy_number: form.policyNumber.trim() || undefined,
    benefit_period: form.benefitPeriod.trim() || undefined,
  };
}
